import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import classNames from "classnames";
import * as React from "react";

import styles from "./Collapsible.module.css";

interface CollapsibleProps {
  title: string;
  /** Whether the section starts expanded. Uncontrolled — subsequent
   *  toggles are driven by the native `<details>` element. */
  defaultOpen?: boolean;
  /** Optional muted helper line under the title. */
  subtitle?: React.ReactNode;
  /** Optional content (e.g. a chip count) rendered on the right side of
   *  the header. Click events are swallowed so clicking a button there
   *  doesn't accidentally toggle the section. */
  headerActions?: React.ReactNode;
  /** Extra class on the container, e.g. to change its background. */
  className?: string;
  children: React.ReactNode;
}

/**
 * Section container with a native `<details>`/`<summary>` core.
 *
 * We use the platform's disclosure widget rather than a custom open-state
 * hook so keyboard handling (Enter / Space to toggle), screen-reader
 * affordances, and "find in page" expansion all work for free. The native
 * marker is suppressed via CSS and we render our own chevron so the
 * presentation matches the rest of the app.
 */
export function Collapsible({
  title,
  defaultOpen = true,
  subtitle,
  headerActions,
  className,
  children,
}: CollapsibleProps) {
  return (
    <details
      className={classNames(styles.details, className)}
      open={defaultOpen}
    >
      <summary className={styles.summary}>
        <ExpandMoreIcon className={styles.chevron} />
        <div className={styles.titleBlock}>
          <span className={styles.title}>{title}</span>
          {subtitle !== undefined && (
            <span className={styles.subtitle}>{subtitle}</span>
          )}
        </div>
        {headerActions !== undefined && (
          <div className={styles.actions} onClick={(e) => e.stopPropagation()}>
            {headerActions}
          </div>
        )}
      </summary>
      <div className={styles.body}>{children}</div>
    </details>
  );
}
